"use client";

/* La ricerca dell'indice (`.p-cerca` del modello del 19/10/2026): «Esplora
   i dossier: Paesi, settori e infrastrutture», il campo con «Cerca», e
   sotto due righe di filtri, Settori e Territori, ognuno col numero di
   dossier.

   Tutto nell'indirizzo (`?q`, `?cat`, `?place`), e ogni cambio riporta a
   pagina 1 (`hrefReport`). Il testo parte all'invio o al pulsante; un
   filtro è un collegamento, e un secondo clic sul filtro acceso lo spegne.
   Senza JavaScript il campo è un GET verso /report con gli stessi nomi
   (settore e territorio scelti viaggiano nei campi nascosti) e i filtri
   sono collegamenti normali: funziona lo stesso.

   Il modello mostra sei settori e cinque territori. I conteggi vengono da
   `faccetteReport` (lib/report-dati.ts), contati su tutti i report a monte;
   le voci oltre quelle in vista restano raggiungibili con «Altri N», e un
   filtro arrivato dall'indirizzo che non è fra le prime resta in vista,
   acceso. */

import { useRouter } from "next/navigation";
import { useState, type FormEvent } from "react";

import { LinkPagina, scorriAllaSezione } from "@/components/editoriali/AllaSezione";
import { Icona, type NomeIcona } from "@/components/home/Icona";
import { ETICHETTA } from "@/components/home/stile";
import { FUOCO_REP } from "@/components/report/stile";
import type { Voce } from "@/lib/report-dati";
import { REPORT_PATH, hrefReport, it, type StatoReport } from "@/lib/report-raccolta";

export type VoceFiltro = Voce & { icona: NomeIcona; tono: string };

const CHIP =
  "inline-flex items-center gap-1.5 rounded-[2px] border px-3 py-[7px] font-sans text-voce no-underline transition-colors";

function Filtri({
  etichetta,
  voci,
  inVista,
  scelto,
  href,
}: {
  etichetta: string;
  voci: VoceFiltro[];
  inVista: number;
  scelto: string;
  href: (v: string) => string;
}) {
  const [tutti, setTutti] = useState(false);
  if (voci.length === 0) return null;
  const prime = voci.slice(0, inVista);
  /* Il filtro dall'indirizzo resta in vista anche se non è fra le prime. */
  const fuori = scelto && !prime.some((v) => v.name === scelto) ? voci.find((v) => v.name === scelto) : undefined;
  const mostrate = tutti ? voci : fuori ? [...prime, fuori] : prime;
  const restano = voci.length - mostrate.length;
  return (
    <div role="group" aria-label={etichetta} className="flex flex-wrap items-center gap-2">
      <span className={`min-w-[76px] mr-1 ${ETICHETTA}`}>{etichetta}</span>
      {mostrate.map((v) => {
        const on = v.name === scelto;
        return (
          <LinkPagina
            key={v.name}
            href={href(on ? "" : v.name)}
            aria-current={on ? "true" : undefined}
            className={`${CHIP} ${
              on ? "border-mare bg-mare text-suMare" : "border-rule text-ink hover:border-mare"
            } ${FUOCO_REP}`}
          >
            <Icona nome={v.icona} tono={on ? undefined : v.tono} />
            {v.name}
            <span className={`font-mono text-meta ${on ? "text-suMare" : "text-sommesso"}`}>{it(v.count)}</span>
          </LinkPagina>
        );
      })}
      {restano > 0 || tutti ? (
        <button
          type="button"
          onClick={() => setTutti((t) => !t)}
          aria-expanded={tutti}
          className={`cursor-pointer border-0 bg-transparent px-1 py-[7px] font-sans text-voce font-semibold text-mare underline-offset-2 hover:underline ${FUOCO_REP}`}
        >
          {tutti ? "Meno" : `Altri ${it(restano)}`}
        </button>
      ) : null}
    </div>
  );
}

export function RicercaReport({
  stato,
  settori,
  territori,
}: {
  stato: StatoReport;
  settori: VoceFiltro[];
  territori: VoceFiltro[];
}) {
  const router = useRouter();
  const [testo, setTesto] = useState(stato.q);
  /* Specchio riallineato all'indirizzo durante il render (la forma di
     <RicercaEditoriali>): tornando indietro o azzerando i filtri il campo
     riprende il valore vero. */
  const [qPrec, setQPrec] = useState(stato.q);
  if (stato.q !== qPrec) {
    setQPrec(stato.q);
    setTesto(stato.q);
  }

  const cerca = (e: FormEvent) => {
    e.preventDefault();
    scorriAllaSezione();
    router.push(hrefReport(stato, { q: testo.trim() }), { scroll: false });
  };

  return (
    <div
      role="search"
      aria-label="Cerca nei report"
      className="mt-6 flex flex-col gap-3 rounded-[3px] border border-rule bg-surface px-5 py-4"
    >
      <form action={REPORT_PATH} method="get" onSubmit={cerca} className="flex flex-col gap-3">
        <label htmlFor="rp-q" className={ETICHETTA}>
          Esplora i report: Paesi, settori e infrastrutture
        </label>
        <div className="flex">
          <input
            id="rp-q"
            name="q"
            type="search"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Un nome, un luogo, una parola"
            autoComplete="off"
            enterKeyHint="search"
            /* 16 pixel sul telefono e non 15: sotto i sedici iOS ingrandisce
               la pagina appena il campo prende il fuoco. */
            className={`min-w-0 flex-1 rounded-l-[2px] border border-r-0 border-sommesso bg-bg px-3.5 py-3 font-sans text-[16px] text-ink placeholder:text-sommesso min-[901px]:text-[15px] ${FUOCO_REP}`}
          />
          {stato.cat ? <input type="hidden" name="cat" value={stato.cat} /> : null}
          {stato.place ? <input type="hidden" name="place" value={stato.place} /> : null}
          <button
            type="submit"
            className={`cursor-pointer rounded-r-[2px] border-0 bg-mare px-5 font-sans text-voce font-semibold text-suMare transition-opacity hover:opacity-90 ${FUOCO_REP}`}
          >
            Cerca <span aria-hidden>↗</span>
          </button>
        </div>
      </form>
      <Filtri
        etichetta="Settori"
        voci={settori}
        inVista={6}
        scelto={stato.cat}
        href={(v) => hrefReport(stato, { cat: v })}
      />
      <Filtri
        etichetta="Territori"
        voci={territori}
        inVista={5}
        scelto={stato.place}
        href={(v) => hrefReport(stato, { place: v })}
      />
    </div>
  );
}
